<template>
  <BaseModal
    :is-visible="isVisible"
    title="Staff Documents & Credentials"
    :subtitle="employee ? `${employee.first_name} ${employee.last_name}'s credentials, certificates & files` : 'Manage employee documents'"
    :icon="FolderLock"
    size="4xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >
    <div class="space-y-5">
      <div v-if="employee?.is_former_employee" class="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/20">
        <Archive class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        <div>
          <p class="text-xs font-bold text-amber-900 dark:text-amber-200">Former Employee Record Linked</p>
          <p class="mt-0.5 text-xs text-amber-800 dark:text-amber-300">
            This account is linked to an archived former-employee record{{ employee.former_employee?.date_separated ? ` separated on ${employee.former_employee.date_separated}` : '' }}{{ employee.former_employee?.reason_for_leaving ? ` due to ${employee.former_employee.reason_for_leaving.toLowerCase()}` : '' }}.
            Documents shown below are current employee documents; archived former-employee files are maintained separately.
          </p>
        </div>
      </div>

      <!-- Top Action Bar -->
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pb-2 border-b border-gray-200 dark:border-gray-700">
        <!-- Search -->
        <div class="relative flex-1 max-w-sm">
          <Search class="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search documents by name or file..."
            class="w-full pl-9 pr-3.5 py-2 text-xs sm:text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none"
          />
        </div>

        <!-- Upload Button -->
        <div class="flex items-center gap-2">
          <BaseButton
            text="Upload Documents"
            variant="primary"
            size="sm"
            :icon="UploadCloud"
            @click="$emit('upload', employee)"
          />
        </div>
      </div>

      <!-- Category Filter Pills (Wrapping without horizontal scrollbar) -->
      <div class="flex flex-wrap items-center gap-1.5 pb-1">
        <button
          type="button"
          @click="selectedCategory = 'all'"
          :class="[
            'px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex items-center gap-1.5',
            selectedCategory === 'all'
              ? 'bg-secondary text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
          ]"
        >
          <span>All Documents</span>
          <span class="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-white/20">
            {{ documents.length + formerFiles.length }}
          </span>
        </button>

        <!-- Expired Filter Tab (Critical Red) -->
        <button
          v-if="expiredCount > 0"
          type="button"
          @click="selectedCategory = selectedCategory === 'expired' ? 'all' : 'expired'"
          :class="[
            'px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex items-center gap-1.5',
            selectedCategory === 'expired'
              ? 'bg-red-600 text-white shadow-sm'
              : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 border border-red-200 dark:border-red-800'
          ]"
        >
          <AlertTriangle class="w-3.5 h-3.5" />
          <span>Expired</span>
          <span
            class="px-1.5 py-0.2 rounded-full text-[10px] font-bold"
            :class="selectedCategory === 'expired' ? 'bg-white/20 text-white' : 'bg-red-200 dark:bg-red-800 text-red-900 dark:text-red-100'"
          >
            {{ expiredCount }}
          </span>
        </button>

        <!-- Expiring Soon Filter Tab (Amber) -->
        <button
          v-if="expiringSoonCount > 0"
          type="button"
          @click="selectedCategory = selectedCategory === 'expiring_soon' ? 'all' : 'expiring_soon'"
          :class="[
            'px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex items-center gap-1.5',
            selectedCategory === 'expiring_soon'
              ? 'bg-amber-500 text-white shadow-sm'
              : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 border border-amber-200 dark:border-amber-800'
          ]"
        >
          <Clock class="w-3.5 h-3.5" />
          <span>Expiring Soon</span>
          <span
            class="px-1.5 py-0.2 rounded-full text-[10px] font-bold"
            :class="selectedCategory === 'expiring_soon' ? 'bg-white/20 text-white' : 'bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100'"
          >
            {{ expiringSoonCount }}
          </span>
        </button>

        <!-- All Needs Attention Filter Tab (Combined when both expired and expiring soon exist) -->
        <button
          v-if="expiredCount > 0 && expiringSoonCount > 0"
          type="button"
          @click="selectedCategory = selectedCategory === 'needs_attention' ? 'all' : 'needs_attention'"
          :class="[
            'px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex items-center gap-1.5',
            selectedCategory === 'needs_attention'
              ? 'bg-amber-600 text-white shadow-sm'
              : 'bg-amber-50/60 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/30 border border-amber-300/80 dark:border-amber-800/60'
          ]"
          title="View both expired and expiring soon documents"
        >
          <Clock class="w-3.5 h-3.5" />
          <span>All Needs Attention</span>
          <span
            class="px-1.5 py-0.2 rounded-full text-[10px] font-bold"
            :class="selectedCategory === 'needs_attention' ? 'bg-white/20 text-white' : 'bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100'"
          >
            {{ expiredOrExpiringCount }}
          </span>
        </button>

        <button
          v-for="cat in DOCUMENT_CATEGORIES"
          :key="cat.value"
          type="button"
          @click="selectedCategory = cat.value"
          :class="[
            'px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 flex items-center gap-1.5',
            selectedCategory === cat.value
              ? 'bg-secondary text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
          ]"
        >
          <span>{{ cat.label }}</span>
          <span
            v-if="categoryCounts[cat.value]"
            class="px-1.5 py-0.2 rounded-full text-[10px] font-bold"
            :class="selectedCategory === cat.value ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'"
          >
            {{ categoryCounts[cat.value] }}
          </span>
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading || isLoadingFormerFiles" class="py-12 flex flex-col items-center justify-center space-y-3">
        <div class="w-8 h-8 border-3 border-secondary border-t-transparent rounded-full animate-spin"></div>
        <p class="text-xs text-gray-500 dark:text-gray-400">Loading documents...</p>
      </div>

      <!-- Former Employee Archive Files -->
      <div
        v-if="!isLoading && !isLoadingFormerFiles && employee?.is_former_employee"
        class="relative space-y-3 rounded-2xl border-2 p-4 transition"
        :class="isArchiveDragOver
          ? 'border-amber-500 bg-amber-50 dark:border-amber-400 dark:bg-amber-950/30'
          : 'border-sky-200 bg-sky-50/50 dark:border-sky-900/50 dark:bg-sky-950/20'"
        @dragenter.prevent="handleArchiveDragEnter"
        @dragover.prevent="isArchiveDragOver = true"
        @dragleave.prevent="handleArchiveDragLeave"
        @drop.prevent="handleArchiveDrop"
      >
        <div class="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex items-center gap-2">
            <Archive class="h-4 w-4 text-sky-600 dark:text-sky-400" />
            <div>
              <h4 class="text-sm font-bold text-sky-900 dark:text-sky-200">Former Employee Archive</h4>
              <p class="text-xs text-sky-700 dark:text-sky-300">{{ filteredFormerFiles.length }} archived file(s), separate from current documents</p>
            </div>
          </div>
          <span class="whitespace-nowrap rounded-full border border-sky-200 bg-white px-2.5 py-1.5 text-[10px] font-bold text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300">Former Employee Record</span>
        </div>

        <div v-if="isArchiveDragOver" class="pointer-events-none absolute inset-3 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-amber-400 bg-amber-100/85 px-4 py-5 text-center text-sm font-bold text-amber-800 shadow-sm backdrop-blur-[1px] dark:border-amber-500 dark:bg-amber-950/75 dark:text-amber-200">
          Drop document here to move it to the archive
        </div>

        <div v-if="filteredFormerFiles.length" class="space-y-2" :class="{ 'opacity-30': isArchiveDragOver }">
          <div v-for="file in filteredFormerFiles" :key="`former-${file.file_id}`" class="flex max-w-full flex-wrap items-start gap-3 overflow-hidden rounded-xl border border-sky-100 bg-white p-3 dark:border-sky-900/50 dark:bg-gray-800">
            <FileText class="h-5 w-5 flex-shrink-0 text-sky-600 dark:text-sky-400" />
            <div class="min-w-0 max-w-full flex-1">
              <p class="truncate text-sm font-semibold text-gray-800 dark:text-gray-200">{{ file.file_label || file.file_name }}</p>
              <p class="max-w-full break-all text-xs leading-4 text-gray-500 dark:text-gray-400">{{ file.file_name }} · {{ formatFileSize(file.file_size) }} · {{ getCategoryLabel(file.file_category || 'other') }}</p>
            </div>
            <div class="flex w-full items-center justify-end gap-2 sm:w-auto">
              <button type="button" class="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300" title="Download archived file" @click="downloadFormerFile(file)">
                <Download class="h-3.5 w-3.5" /> Download
              </button>
              <button type="button" class="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-700 transition hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300" title="Move back to current documents" @click="requestRestoreFormerFile(file)">
                <Undo2 class="h-3.5 w-3.5" /> Restore
              </button>
            </div>
          </div>
        </div>

        <div v-if="!filteredFormerFiles.length" class="rounded-xl border border-dashed border-sky-200 px-4 py-5 text-center text-xs text-sky-700 dark:border-sky-800 dark:text-sky-300" :class="{ 'opacity-30': isArchiveDragOver }">
          Drag a current document here to add the first archived file.
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="!isLoading && !isLoadingFormerFiles && filteredDocuments.length === 0 && filteredFormerFiles.length === 0"
        class="py-12 px-4 text-center rounded-2xl bg-gray-50 dark:bg-gray-800/40 border border-dashed border-gray-300 dark:border-gray-700 space-y-3"
      >
        <div class="w-12 h-12 mx-auto rounded-2xl bg-gray-100 dark:bg-gray-700/60 text-gray-400 flex items-center justify-center">
          <FolderOpen class="w-6 h-6" />
        </div>
        <div>
          <h4 class="text-sm font-bold text-gray-800 dark:text-gray-200">No documents found</h4>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
            {{ searchQuery ? 'No documents match your search query.' : 'There are no files uploaded in this category for this staff member.' }}
          </p>
        </div>
        <div class="pt-1">
          <BaseButton
            text="Upload Document"
            variant="outline"
            size="sm"
            :icon="UploadCloud"
            @click="$emit('upload', employee)"
          />
        </div>
      </div>

      <!-- Documents Container Cards (No Horizontal Scrollbar, Actions below each container) -->
      <div v-if="!isLoading && !isLoadingFormerFiles && filteredDocuments.length > 0" class="space-y-3 max-h-[500px] overflow-y-auto pr-1 transition-opacity" :class="{ 'opacity-50': isArchiveDragOver }">
        <div
          v-for="doc in filteredDocuments"
          :key="doc.document_id"
          :draggable="Boolean(employee?.is_former_employee)"
          @dragstart="startDocumentDrag(doc)"
          @dragend="endDocumentDrag"
          :class="{ 'cursor-grab active:cursor-grabbing': employee?.is_former_employee }"
          class="p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xs hover:border-secondary/40 transition duration-150 space-y-3"
        >
          <!-- Top Row: File Details & Badges -->
          <div class="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <!-- Left: Icon + Title + Meta -->
            <div class="flex items-start gap-3 min-w-0">
              <div class="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex-shrink-0 mt-0.5">
                <FileText v-if="isPdf(doc)" class="w-5 h-5 text-red-500" />
                <FileText v-else-if="isDoc(doc)" class="w-5 h-5 text-blue-500" />
                <ImageIcon v-else-if="isImage(doc)" class="w-5 h-5 text-emerald-500" />
                <Paperclip v-else class="w-5 h-5 text-gray-500" />
              </div>

              <div class="min-w-0 space-y-1">
                <div class="flex flex-wrap items-center gap-2">
                  <h4 class="text-sm font-bold text-gray-900 dark:text-white break-words" :title="doc.document_name">
                    {{ doc.document_name }}
                  </h4>
                  <span
                    :class="[
                      'px-2 py-0.5 rounded-md text-[10px] font-bold border sm:hidden',
                      getCategoryBadgeClass(doc.document_category)
                    ]"
                  >
                    {{ getCategoryLabel(doc.document_category) }}
                  </span>
                </div>

                <div class="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                  <span class="font-medium text-gray-700 dark:text-gray-300 truncate max-w-[200px]" :title="doc.file_name">
                    {{ doc.file_name }}
                  </span>
                  <span>•</span>
                  <span>{{ formatFileSize(doc.file_size) }}</span>
                  <span>•</span>
                  <span>Uploaded by {{ doc.uploader_name || 'Administrator' }} on {{ formatDate(doc.created_at) }}</span>
                </div>

                <p v-if="doc.notes" class="text-xs text-secondary dark:text-secondary-dark-text italic pt-0.5">
                  Note: {{ doc.notes }}
                </p>
              </div>
            </div>

            <!-- Right: Category & Expiry Badges (Desktop) -->
            <div class="flex flex-wrap sm:flex-col sm:items-end gap-2 flex-shrink-0">
              <!-- Category badge -->
              <span
                :class="[
                  'hidden sm:inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border',
                  getCategoryBadgeClass(doc.document_category)
                ]"
              >
                {{ getCategoryLabel(doc.document_category) }}
              </span>

              <!-- Expiration status -->
              <div
                v-if="doc.expiry_date"
                class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-xs font-medium bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700"
                :title="doc.is_expired || isDocumentExpired(doc.expiry_date) ? 'Document has expired' : doc.is_expiring_soon || isDocumentExpiringSoon(doc.expiry_date) ? 'Expiry Notice: Document expires in less than 1 year' : 'Valid and active'"
              >
                <span
                  :class="[
                    'w-2 h-2 rounded-full',
                    doc.is_expired || isDocumentExpired(doc.expiry_date)
                      ? 'bg-red-500'
                      : doc.is_expiring_soon || isDocumentExpiringSoon(doc.expiry_date)
                        ? 'bg-amber-500'
                        : 'bg-green-500'
                  ]"
                ></span>
                <span
                  :class="[
                    'font-semibold',
                    doc.is_expired || isDocumentExpired(doc.expiry_date)
                      ? 'text-red-600 dark:text-red-400'
                      : doc.is_expiring_soon || isDocumentExpiringSoon(doc.expiry_date)
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-gray-700 dark:text-gray-300'
                  ]"
                >
                  {{ doc.is_expired || isDocumentExpired(doc.expiry_date) ? 'Expired' : 'Expires' }}:
                </span>
                <span class="text-gray-600 dark:text-gray-400 font-mono">{{ formatDate(doc.expiry_date) }}</span>
                <span
                  v-if="!doc.is_expired && !isDocumentExpired(doc.expiry_date) && (doc.is_expiring_soon || isDocumentExpiringSoon(doc.expiry_date))"
                  class="text-[10px] font-bold text-amber-600 dark:text-amber-400 ml-0.5"
                >
                  (&lt; 1 yr)
                </span>
              </div>
              <span v-else class="text-[11px] text-gray-400 dark:text-gray-500">
                No expiration date
              </span>
            </div>
          </div>

          <!-- Bottom Action Bar: Put actions directly below each file container with UI/UX in mind -->
          <div class="pt-2.5 border-t border-gray-100 dark:border-gray-700/70 flex flex-wrap items-center justify-between gap-2">
            <span class="text-[11px] text-gray-400 dark:text-gray-500">
              ID: #{{ doc.document_id }}
            </span>

            <div class="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <!-- Preview -->
              <button
                type="button"
                @click="previewDocument(doc)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-secondary hover:bg-secondary/10 dark:text-secondary-dark-text dark:hover:bg-secondary-dark/20 border border-secondary/20 transition"
                title="Preview Document Inline"
              >
                <Eye class="w-3.5 h-3.5" />
                <span>Preview</span>
              </button>

              <!-- Download -->
              <button
                type="button"
                @click="downloadDocument(doc)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 transition"
                title="Download Document"
              >
                <Download class="w-3.5 h-3.5" />
                <span>Download</span>
              </button>

              <!-- Edit Metadata -->
              <button
                type="button"
                @click="openEditModal(doc)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-300 dark:bg-blue-950/40 dark:hover:bg-blue-900/40 border border-blue-200 dark:border-blue-800 transition"
                title="Edit Document Details"
              >
                <Edit3 class="w-3.5 h-3.5" />
                <span>Edit</span>
              </button>

              <!-- Replace File -->
              <button
                type="button"
                :disabled="replacingDocumentId === doc.document_id"
                @click="triggerReplaceInput(doc)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 dark:text-amber-300 dark:bg-amber-950/40 dark:hover:bg-amber-900/40 border border-amber-200 dark:border-amber-800 transition disabled:cursor-not-allowed disabled:opacity-50"
                title="Replace Document File"
              >
                <RefreshCw class="w-3.5 h-3.5" :class="{ 'animate-spin': replacingDocumentId === doc.document_id }" />
                <span>{{ replacingDocumentId === doc.document_id ? 'Replacing...' : 'Replace' }}</span>
              </button>

              <!-- Move to Former Employee Archive -->
              <button
                v-if="employee?.is_former_employee"
                type="button"
                :disabled="archivingDocumentId === doc.document_id"
                @click="requestArchiveDocument(doc)"
                class="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-900/40"
                title="Move this document to the former-employee archive"
              >
                <Archive class="h-3.5 w-3.5" />
                <span>{{ archivingDocumentId === doc.document_id ? 'Moving...' : 'Archive' }}</span>
              </button>

              <!-- Delete -->
              <button
                type="button"
                @click="confirmDeleteDocument(doc)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-950/40 dark:hover:bg-red-900/40 border border-red-200 dark:border-red-800 transition"
                title="Delete Document"
              >
                <Trash2 class="w-3.5 h-3.5" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden input for file replacement -->
    <input
      ref="replaceFileInputRef"
      type="file"
      class="hidden"
      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
      @change="handleReplaceFileSelected"
    />

    <!-- Edit Metadata Sub-Modal -->
    <BaseModal
      :is-visible="isEditModalVisible"
      title="Edit Document Information"
      subtitle="Update document name, category, or expiration date"
      size="lg"
      type="default"
      :show-footer="true"
      @close="isEditModalVisible = false"
    >
      <div v-if="selectedDocForEdit" class="space-y-4">
        <div>
          <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
            Document Title <span class="text-red-500">*</span>
          </label>
          <input
            v-model="editForm.document_name"
            type="text"
            class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary"
            required
          />
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Category <span class="text-red-500">*</span>
            </label>
            <select
              v-model="editForm.document_category"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary"
            >
              <option
                v-for="cat in DOCUMENT_CATEGORIES"
                :key="cat.value"
                :value="cat.value"
              >
                {{ cat.label }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Expiration Date
            </label>
            <input
              v-model="editForm.expiry_date"
              type="date"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary"
            />
          </div>
        </div>

        <div>
          <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
            Notes / Remarks
          </label>
          <textarea
            v-model="editForm.notes"
            rows="2"
            class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-secondary"
            placeholder="Add internal notes"
          ></textarea>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-2 w-full">
          <BaseButton
            text="Cancel"
            variant="outline"
            size="sm"
            @click="isEditModalVisible = false"
          />
          <BaseButton
            text="Save Changes"
            variant="primary"
            size="sm"
            :loading="isSavingEdit"
            @click="handleSaveEdit"
          />
        </div>
      </template>
    </BaseModal>

    <!-- Document Inline Preview Sub-Modal -->
    <BaseModal
      :is-visible="isPreviewModalVisible"
      :title="previewDoc?.document_name || 'Document Preview'"
      :subtitle="previewDoc ? `${previewDoc.file_name} (${formatFileSize(previewDoc.file_size)})` : 'File Preview'"
      size="4xl"
      type="default"
      :show-footer="true"
      @close="closePreviewModal"
    >
      <div class="space-y-4">
        <div v-if="isLoadingPreview" class="py-16 flex flex-col items-center justify-center space-y-3">
          <div class="w-8 h-8 border-3 border-secondary border-t-transparent rounded-full animate-spin"></div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Loading file preview...</p>
        </div>

        <div v-else-if="previewBlobUrl" class="w-full">
          <!-- Image preview -->
          <div
            v-if="isImagePreview"
            class="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 flex justify-center items-center min-h-[400px] max-h-[600px]"
          >
            <img
              :src="previewBlobUrl"
              :alt="previewDoc?.document_name"
              class="max-w-full max-h-[580px] object-contain p-2"
            />
          </div>

          <!-- Docx preview -->
          <div v-else-if="isDocxPreview" class="w-full">
            <DocxViewer
              :src="previewBlob || previewBlobUrl"
              :file-name="previewDoc?.file_name"
              max-height="600px"
              :allow-download="false"
              @download="previewDoc ? downloadDocument(previewDoc) : undefined"
            />
          </div>

          <!-- PDF preview iframe -->
          <div
            v-else-if="isPdfPreview"
            class="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 flex justify-center items-center min-h-[400px]"
          >
            <iframe
              :src="previewBlobUrl"
              class="w-full h-[550px] border-none"
              title="PDF Preview"
            ></iframe>
          </div>

          <!-- Fallback -->
          <div v-else class="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 p-8 text-center space-y-3">
            <FileText class="w-12 h-12 text-gray-400 mx-auto" />
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
              Preview is not available for this file type ({{ previewDoc?.file_name }}).
            </p>
            <BaseButton
              v-if="previewDoc"
              text="Download to View"
              variant="primary"
              size="sm"
              :icon="Download"
              @click="downloadDocument(previewDoc)"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-between w-full">
          <BaseButton
            v-if="previewDoc"
            text="Download"
            variant="outline"
            size="sm"
            :icon="Download"
            @click="downloadDocument(previewDoc)"
          />
          <div v-else></div>
          <BaseButton
            text="Close"
            variant="primary"
            size="sm"
            @click="closePreviewModal"
          />
        </div>
      </template>
    </BaseModal>

    <!-- Archive Confirmation Sub-Modal -->
    <BaseModal
      :is-visible="isArchiveConfirmVisible"
      title="Move Document to Archive"
      subtitle="Confirm archive action"
      :icon="Archive"
      size="md"
      type="warning"
      :show-footer="true"
      @close="closeArchiveConfirmation"
    >
      <div v-if="documentToArchive" class="space-y-3">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Move <span class="font-bold text-gray-900 dark:text-white">"{{ documentToArchive.document_name }}"</span> to the former-employee archive?
        </p>
        <p class="text-xs text-amber-700 dark:text-amber-300">
          This document will be removed from the current employee documents and stored under Former Employee Records.
        </p>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-2 w-full">
          <BaseButton
            text="Cancel"
            variant="outline"
            size="sm"
            :disabled="Boolean(archivingDocumentId)"
            @click="closeArchiveConfirmation"
          />
          <BaseButton
            text="Move to Archive"
            variant="warning"
            size="sm"
            :icon="Archive"
            :loading="Boolean(archivingDocumentId)"
            @click="archiveDocument"
          />
        </div>
      </template>
    </BaseModal>

    <BaseModal
      :is-visible="isRestoreConfirmVisible"
      title="Move File to Current Documents"
      subtitle="Confirm restore action"
      :icon="Undo2"
      size="md"
      type="warning"
      :show-footer="true"
      @close="closeRestoreConfirmation"
    >
      <div v-if="fileToRestore" class="space-y-3">
        <p class="min-w-0 max-w-full break-words text-sm text-gray-700 dark:text-gray-300">
          Move <span class="break-all font-bold text-gray-900 dark:text-white">"{{ fileToRestore.file_name }}"</span> back to current documents?
        </p>
        <p class="text-xs text-amber-700 dark:text-amber-300">The file will be removed from the former-employee archive and restored to the current document list.</p>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-2 w-full">
          <BaseButton text="Cancel" variant="outline" size="sm" :disabled="isRestoring" @click="closeRestoreConfirmation" />
          <BaseButton text="Move to Current" variant="warning" size="sm" :icon="Undo2" :loading="isRestoring" @click="restoreFormerFile" />
        </div>
      </template>
    </BaseModal>

    <!-- Delete Confirmation Sub-Modal -->
    <BaseModal
      :is-visible="isDeleteModalVisible"
      title="Delete Document"
      subtitle="Confirm deletion"
      size="md"
      type="danger"
      :show-footer="true"
      @close="isDeleteModalVisible = false"
    >
      <div class="space-y-3">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Are you sure you want to delete
          <span class="font-bold text-gray-900 dark:text-white">"{{ docToDelete?.document_name }}"</span>?
        </p>
        <p class="text-xs text-red-600 dark:text-red-400">
          This will permanently remove the uploaded file from the server. This action cannot be undone.
        </p>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-2 w-full">
          <BaseButton
            text="Cancel"
            variant="outline"
            size="sm"
            @click="isDeleteModalVisible = false"
          />
          <BaseButton
            text="Delete Document"
            variant="danger"
            size="sm"
            :loading="isDeleting"
            @click="handleDeleteDocument"
          />
        </div>
      </template>
    </BaseModal>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <div class="text-xs font-medium text-gray-500 dark:text-gray-400">
          Total: <span class="font-bold text-gray-900 dark:text-white">{{ documents.length }}</span> current document(s)
          <span v-if="formerFiles.length"> · <span class="font-bold text-sky-700 dark:text-sky-300">{{ formerFiles.length }}</span> archived file(s)</span>
        </div>
        <BaseButton
          text="Close"
          variant="primary"
          size="sm"
          @click="closeModal"
        />
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import DocxViewer from '@/components/common/DocxViewer.vue'
import { employeeDocumentService } from '@/services/employeeDocumentService'
import formerEmployeeService from '@/services/formerEmployeeService'
import { useToastService } from '@/services/toast'
import type { employee } from '@/types/employee'
import type { FormerEmployeeFile } from '@/types/formerEmployee'
import {
  DOCUMENT_CATEGORIES,
  type DocumentCategory,
  type EmployeeDocument,
  documentNeedsAttention,
  isDocumentExpired,
  isDocumentExpiringSoon,
} from '@/types/employeeDocument'
import {
  FolderLock,
  Search,
  UploadCloud,
  FolderOpen,
  FileText,
  ImageIcon,
  Paperclip,
  Eye,
  Download,
  Edit3,
  RefreshCw,
  Trash2,
  Clock,
  AlertTriangle,
  Archive,
  Undo2,
} from 'lucide-vue-next'

const props = defineProps<{
  isVisible: boolean
  employee: employee | null
  initialCategory?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'upload', employee: employee | null): void
  (e: 'updated', count: number): void
}>()

const toast = useToastService()

const documents = ref<EmployeeDocument[]>([])
const formerFiles = ref<FormerEmployeeFile[]>([])
const isLoading = ref(false)
const isLoadingFormerFiles = ref(false)
const isArchiveDragOver = ref(false)
const archiveDragDepth = ref(0)
const draggedDocument = ref<EmployeeDocument | null>(null)
const archivingDocumentId = ref<number | null>(null)
const isArchiveConfirmVisible = ref(false)
const documentToArchive = ref<EmployeeDocument | null>(null)
const isRestoreConfirmVisible = ref(false)
const fileToRestore = ref<FormerEmployeeFile | null>(null)
const isRestoring = ref(false)
const searchQuery = ref('')
const selectedCategory = ref<string>('all')

// Edit state
const isEditModalVisible = ref(false)
const selectedDocForEdit = ref<EmployeeDocument | null>(null)
const isSavingEdit = ref(false)
const editForm = ref({
  document_name: '',
  document_category: 'other' as DocumentCategory,
  notes: '',
  expiry_date: '',
})

// Replace state
const replaceFileInputRef = ref<HTMLInputElement | null>(null)
const docToReplace = ref<EmployeeDocument | null>(null)
const replacingDocumentId = ref<number | null>(null)

// Preview state
const isPreviewModalVisible = ref(false)
const previewDoc = ref<EmployeeDocument | null>(null)
const previewBlobUrl = ref<string | null>(null)
const previewBlob = ref<Blob | null>(null)
const previewMimeType = ref<string>('application/pdf')
const isLoadingPreview = ref(false)

const isPdfPreview = computed(() => {
  if (!previewDoc.value) return false
  const ext = previewDoc.value.file_name.toLowerCase()
  return ext.endsWith('.pdf') || previewMimeType.value === 'application/pdf'
})

const isImagePreview = computed(() => {
  if (!previewDoc.value) return false
  const ext = previewDoc.value.file_name.toLowerCase()
  return (
    ext.endsWith('.jpg') ||
    ext.endsWith('.jpeg') ||
    ext.endsWith('.png') ||
    ext.endsWith('.webp') ||
    ext.endsWith('.gif') ||
    previewMimeType.value.startsWith('image/')
  )
})

const isDocxPreview = computed(() => {
  if (!previewDoc.value) return false
  const ext = previewDoc.value.file_name.toLowerCase()
  return (
    ext.endsWith('.docx') ||
    ext.endsWith('.doc') ||
    previewMimeType.value === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    previewMimeType.value === 'application/msword'
  )
})

// Delete state
const isDeleteModalVisible = ref(false)
const docToDelete = ref<EmployeeDocument | null>(null)
const isDeleting = ref(false)

// Fetch documents on open
watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      selectedCategory.value = props.initialCategory || 'all'
    }
    if (visible && props.employee?.user_id) {
      fetchDocuments()
    }
    if (visible && props.employee?.former_employee?.former_employee_id) {
      fetchFormerFiles()
    }
    if (!visible) {
      formerFiles.value = []
    }
  },
  { immediate: true }
)

watch(
  () => props.initialCategory,
  (newCat) => {
    if (props.isVisible && newCat) {
      selectedCategory.value = newCat
    }
  }
)

const fetchDocuments = async () => {
  if (!props.employee?.user_id) return

  isLoading.value = true
  try {
    const list = await employeeDocumentService.getDocuments(props.employee.user_id)
    documents.value = list
    emit('updated', list.length)
  } catch (err) {
    toast.error('Failed to load employee documents.')
  } finally {
    isLoading.value = false
  }
}

const fetchFormerFiles = async () => {
  const formerEmployeeId = props.employee?.former_employee?.former_employee_id
  if (!formerEmployeeId) return

  isLoadingFormerFiles.value = true
  try {
    const record = await formerEmployeeService.getFormerEmployee(formerEmployeeId)
    formerFiles.value = record.files || []
  } catch (err) {
    formerFiles.value = []
    toast.error('Failed to load archived former-employee files.')
  } finally {
    isLoadingFormerFiles.value = false
  }
}

// Category counts
const categoryCounts = computed(() => {
  const counts: Record<string, number> = {}
  documents.value.forEach(doc => {
    counts[doc.document_category] = (counts[doc.document_category] || 0) + 1
  })
  return counts
})

// Expired documents count
const expiredCount = computed(() => {
  return documents.value.filter(d => isDocumentExpired(d.expiry_date)).length
})

// Expiring soon documents count (< 1 year before expiry)
const expiringSoonCount = computed(() => {
  return documents.value.filter(d => isDocumentExpiringSoon(d.expiry_date)).length
})

// Documents needing attention (expired or < 1 year before expiry)
const expiredOrExpiringCount = computed(() => {
  return documents.value.filter(d => documentNeedsAttention(d)).length
})

// Filtered list
const filteredDocuments = computed(() => {
  let list = documents.value

  if (selectedCategory.value === 'expired') {
    list = list.filter(d => isDocumentExpired(d.expiry_date))
  } else if (selectedCategory.value === 'expiring_soon') {
    list = list.filter(d => isDocumentExpiringSoon(d.expiry_date))
  } else if (selectedCategory.value === 'needs_attention') {
    list = list.filter(d => documentNeedsAttention(d))
  } else if (selectedCategory.value !== 'all') {
    list = list.filter(d => d.document_category === selectedCategory.value)
  }

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase().trim()
    list = list.filter(d =>
      d.document_name.toLowerCase().includes(q) ||
      d.file_name.toLowerCase().includes(q) ||
      (d.notes && d.notes.toLowerCase().includes(q))
    )
  }

  return list
})

const filteredFormerFiles = computed(() => {
  let list = formerFiles.value

  if (['needs_attention', 'expired', 'expiring_soon'].includes(selectedCategory.value)) {
    return []
  } else if (selectedCategory.value !== 'all') {
    list = list.filter(file => file.file_category === selectedCategory.value)
  }

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase().trim()
    list = list.filter(file =>
      file.file_name.toLowerCase().includes(q) ||
      (file.file_label && file.file_label.toLowerCase().includes(q))
    )
  }

  return list
})

const getCategoryLabel = (category: string) => {
  const item = DOCUMENT_CATEGORIES.find(c => c.value === category)
  return item ? item.label : category
}

const getCategoryBadgeClass = (category: string) => {
  const item = DOCUMENT_CATEGORIES.find(c => c.value === category)
  return item ? item.badgeClass : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

const formatFileSize = (bytes: number | null) => {
  if (!bytes) return '0 B'
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return 'N/A'
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateStr
  }
}

const isPdf = (doc: EmployeeDocument) => doc.file_type === 'application/pdf' || doc.file_name.endsWith('.pdf')
const isDoc = (doc: EmployeeDocument) => doc.file_name.endsWith('.doc') || doc.file_name.endsWith('.docx')
const isImage = (doc: EmployeeDocument) => (doc.file_type && doc.file_type.startsWith('image/')) || /\.(jpg|jpeg|png)$/i.test(doc.file_name)

// Actions
const downloadDocument = async (doc: EmployeeDocument) => {
  if (!props.employee?.user_id) return
  try {
    await employeeDocumentService.downloadDocument(props.employee.user_id, doc.document_id, doc.file_name)
    toast.success(`Downloading ${doc.file_name}`)
  } catch (err) {
    toast.error('Failed to download document.')
  }
}

const downloadFormerFile = async (file: FormerEmployeeFile) => {
  const formerEmployeeId = props.employee?.former_employee?.former_employee_id
  if (!formerEmployeeId) return

  try {
    await formerEmployeeService.downloadFile(formerEmployeeId, file.file_id, file.file_name)
    toast.success(`Downloading ${file.file_name}`)
  } catch (err) {
    toast.error('Failed to download archived file.')
  }
}

const requestRestoreFormerFile = (file: FormerEmployeeFile) => {
  if (!props.employee?.former_employee?.former_employee_id || isRestoring.value) return
  fileToRestore.value = file
  isRestoreConfirmVisible.value = true
}

const closeRestoreConfirmation = () => {
  if (isRestoring.value) return
  isRestoreConfirmVisible.value = false
  fileToRestore.value = null
}

const restoreFormerFile = async () => {
  const formerEmployeeId = props.employee?.former_employee?.former_employee_id
  const file = fileToRestore.value
  if (!formerEmployeeId || !file || isRestoring.value) return

  isRestoring.value = true
  try {
    const restoredDocument = await formerEmployeeService.restoreFile(formerEmployeeId, file.file_id)
    formerFiles.value = formerFiles.value.filter(item => item.file_id !== file.file_id)
    documents.value.unshift(restoredDocument)
    emit('updated', documents.value.length)
    toast.success('File restored', `${file.file_name} was moved to current documents.`)
    isRestoreConfirmVisible.value = false
    fileToRestore.value = null
  } catch (error: any) {
    toast.error('Restore failed', error?.response?.data?.message || 'The archived file could not be restored.')
  } finally {
    isRestoring.value = false
  }
}

const startDocumentDrag = (document: EmployeeDocument) => {
  if (props.employee?.is_former_employee) {
    draggedDocument.value = document
    archiveDragDepth.value = 0
  }
}

const endDocumentDrag = () => {
  draggedDocument.value = null
  archiveDragDepth.value = 0
  isArchiveDragOver.value = false
}

const handleArchiveDragEnter = () => {
  if (!draggedDocument.value) return
  archiveDragDepth.value += 1
  isArchiveDragOver.value = true
}

const handleArchiveDragLeave = () => {
  archiveDragDepth.value = Math.max(0, archiveDragDepth.value - 1)
  if (archiveDragDepth.value === 0) {
    isArchiveDragOver.value = false
  }
}

const handleArchiveDrop = () => {
  isArchiveDragOver.value = false
  archiveDragDepth.value = 0
  if (draggedDocument.value) {
    requestArchiveDocument(draggedDocument.value)
  }
  draggedDocument.value = null
}

const requestArchiveDocument = (document: EmployeeDocument) => {
  if (!props.employee?.user_id || !props.employee.is_former_employee || archivingDocumentId.value) return
  documentToArchive.value = document
  isArchiveConfirmVisible.value = true
}

const closeArchiveConfirmation = () => {
  if (archivingDocumentId.value) return
  isArchiveConfirmVisible.value = false
  documentToArchive.value = null
}

const archiveDocument = async () => {
  const document = documentToArchive.value
  if (!document || !props.employee?.user_id || !props.employee.is_former_employee || archivingDocumentId.value) return

  archivingDocumentId.value = document.document_id
  try {
    const archivedFile = await employeeDocumentService.archiveDocument(props.employee.user_id, document.document_id)
    documents.value = documents.value.filter(item => item.document_id !== document.document_id)
    formerFiles.value.unshift(archivedFile)
    emit('updated', documents.value.length)
    toast.success('Document archived', `${document.document_name} was moved to Former Employee Records.`)
    isArchiveConfirmVisible.value = false
    documentToArchive.value = null
  } catch (error: any) {
    toast.error('Archive failed', error?.response?.data?.message || 'The document could not be moved to the archive.')
  } finally {
    archivingDocumentId.value = null
  }
}

const previewDocument = async (doc: EmployeeDocument) => {
  if (!props.employee?.user_id) return
  previewDoc.value = doc
  isPreviewModalVisible.value = true
  isLoadingPreview.value = true
  previewBlobUrl.value = null
  previewBlob.value = null

  try {
    const { url, mimeType, blob } = await employeeDocumentService.getPreviewBlobUrl(props.employee.user_id, doc.document_id)
    previewBlobUrl.value = url
    previewBlob.value = blob
    previewMimeType.value = mimeType
  } catch (err) {
    toast.error('Could not load inline preview for this file.')
  } finally {
    isLoadingPreview.value = false
  }
}

const closePreviewModal = () => {
  isPreviewModalVisible.value = false
  if (previewBlobUrl.value) {
    window.URL.revokeObjectURL(previewBlobUrl.value)
    previewBlobUrl.value = null
  }
  previewBlob.value = null
  previewDoc.value = null
}

const openEditModal = (doc: EmployeeDocument) => {
  selectedDocForEdit.value = doc
  editForm.value = {
    document_name: doc.document_name,
    document_category: doc.document_category,
    notes: doc.notes || '',
    expiry_date: doc.expiry_date || '',
  }
  isEditModalVisible.value = true
}

const handleSaveEdit = async () => {
  if (!props.employee?.user_id || !selectedDocForEdit.value) return

  isSavingEdit.value = true
  try {
    const updated = await employeeDocumentService.updateDocument(
      props.employee.user_id,
      selectedDocForEdit.value.document_id,
      {
        document_name: editForm.value.document_name,
        document_category: editForm.value.document_category,
        notes: editForm.value.notes || null,
        expiry_date: editForm.value.expiry_date || null,
      }
    )

    // Update locally
    const idx = documents.value.findIndex(d => d.document_id === updated.document_id)
    if (idx !== -1) {
      documents.value[idx] = {
        ...documents.value[idx],
        ...updated,
      }
    }

    toast.success('Document updated successfully.')
    isEditModalVisible.value = false
  } catch (err) {
    toast.error('Failed to update document details.')
  } finally {
    isSavingEdit.value = false
  }
}

const triggerReplaceInput = (doc: EmployeeDocument) => {
  docToReplace.value = doc
  replaceFileInputRef.value?.click()
}

const handleReplaceFileSelected = async (e: Event) => {
  const target = e.target as HTMLInputElement
  if (!target.files || target.files.length === 0 || !docToReplace.value || !props.employee?.user_id) return

  const file = target.files[0]
  if (file.size > 32 * 1024 * 1024) {
    toast.error('File Too Large', `"${file.name}" exceeds the 32MB size limit.`)
    target.value = ''
    return
  }

  const formData = new FormData()
  formData.append('document', file)
  replacingDocumentId.value = docToReplace.value.document_id

  try {
    const updated = await employeeDocumentService.replaceDocument(
      props.employee.user_id,
      docToReplace.value.document_id,
      formData
    )

    const idx = documents.value.findIndex(d => d.document_id === updated.document_id)
    if (idx !== -1) {
      documents.value[idx] = {
        ...documents.value[idx],
        ...updated,
      }
    }

    toast.success(`File replaced with ${file.name}`)
  } catch (err) {
    toast.error('Failed to replace document file.')
  } finally {
    if (replaceFileInputRef.value) {
      replaceFileInputRef.value.value = ''
    }
    docToReplace.value = null
    replacingDocumentId.value = null
  }
}

const confirmDeleteDocument = (doc: EmployeeDocument) => {
  docToDelete.value = doc
  isDeleteModalVisible.value = true
}

const handleDeleteDocument = async () => {
  if (!props.employee?.user_id || !docToDelete.value) return

  isDeleting.value = true
  try {
    const deletedId = docToDelete.value.document_id
    await employeeDocumentService.deleteDocument(props.employee.user_id, deletedId)
    documents.value = documents.value.filter(d => d.document_id !== deletedId)
    emit('updated', documents.value.length)
    toast.success('Document deleted successfully.')
    isDeleteModalVisible.value = false
  } catch (err) {
    toast.error('Failed to delete document.')
  } finally {
    isDeleting.value = false
    docToDelete.value = null
  }
}

const closeModal = () => {
  emit('close')
}

// Expose refresh for parent when upload succeeds
defineExpose({
  fetchDocuments
})
</script>
